import { useState, useEffect, useRef, memo, forwardRef, useImperativeHandle } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

/**
 * FastInput — drop-in replacement for <Input> that decouples typing from
 * parent re-renders.
 *
 * Why? On a ~2000-line form like ProspectForm, every keystroke triggers a
 * full re-render of the page on weak CPUs (tablets). By holding the value
 * locally and only notifying the parent on blur, the parent re-renders
 * exactly once per field instead of once per keystroke.
 *
 * Usage:
 *   <FastInput
 *     defaultValue={formData.nom}
 *     onCommit={(v) => setFormData(p => ({ ...p, nom: v }))}
 *     placeholder="Nom complet"
 *   />
 *
 * The committed value is also pushed up if the user presses Enter.
 *
 * Note: do NOT use this for fields that need real-time validation or that
 * feed live calculations on every char (e.g. inputs in calcul des couts).
 * Stick with controlled inputs there.
 */
const FastInput = memo(forwardRef(function FastInput(
    { defaultValue, onCommit, ...props },
    ref
) {
    const [v, setV] = useState(defaultValue ?? '');
    const inputRef = useRef(null);
    const lastCommittedRef = useRef(defaultValue ?? '');

    // Reset local state if parent pushes a new defaultValue (e.g. data
    // reloaded after a save) AND the user hasn't typed something else.
    useEffect(() => {
        const incoming = defaultValue ?? '';
        if (incoming !== lastCommittedRef.current) {
            setV(incoming);
            lastCommittedRef.current = incoming;
        }
    }, [defaultValue]);

    // Allow parent to imperatively read current value (used by handleSubmit
    // wrapper to flush pending edits before sending).
    useImperativeHandle(ref, () => ({
        getValue: () => v,
        commit: () => {
            if (v !== lastCommittedRef.current) {
                lastCommittedRef.current = v;
                onCommit?.(v);
            }
        },
        focus: () => inputRef.current?.focus(),
    }));

    const commit = () => {
        if (v !== lastCommittedRef.current) {
            lastCommittedRef.current = v;
            onCommit?.(v);
        }
    };

    return (
        <Input
            {...props}
            ref={inputRef}
            value={v}
            onChange={(e) => setV(e.target.value)}
            onBlur={(e) => {
                commit();
                props.onBlur?.(e);
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && props.type !== 'textarea') commit();
                props.onKeyDown?.(e);
            }}
        />
    );
}));

export default FastInput;

export const FastTextarea = memo(forwardRef(function FastTextarea(
    { defaultValue, onCommit, ...props },
    ref
) {
    const [v, setV] = useState(defaultValue ?? '');
    const taRef = useRef(null);
    const lastCommittedRef = useRef(defaultValue ?? '');

    useEffect(() => {
        const incoming = defaultValue ?? '';
        if (incoming !== lastCommittedRef.current) {
            setV(incoming);
            lastCommittedRef.current = incoming;
        }
    }, [defaultValue]);

    useImperativeHandle(ref, () => ({
        getValue: () => v,
        commit: () => {
            if (v !== lastCommittedRef.current) {
                lastCommittedRef.current = v;
                onCommit?.(v);
            }
        },
        focus: () => taRef.current?.focus(),
    }));

    const commit = () => {
        if (v !== lastCommittedRef.current) {
            lastCommittedRef.current = v;
            onCommit?.(v);
        }
    };

    return (
        <Textarea
            {...props}
            ref={taRef}
            value={v}
            onChange={(e) => setV(e.target.value)}
            onBlur={(e) => {
                commit();
                props.onBlur?.(e);
            }}
        />
    );
}));
